import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useNotifications } from '@/features/notifications';

import {
  useUpdateOfferStatusMutation,
  useUpdateOfferFeedbackMutation,
  useUpdateOfferAvailabilityAndStatusMutation,
  useUpdateOfferMeetingTimeMutation,
  useUpdateOfferGeneratedPdfMutation,
  useUpdateOfferSlotManualRequestMutation,
} from '../api';
import { OfferStatus, Rating } from '../types';

export type useOnUpdateOfferProps = {
  id: string;
  opportunity?: string;
  onClose?: () => void;
};

export function useOnUpdateOffer({ id, opportunity, onClose }: useOnUpdateOfferProps) {
  const { t } = useTranslation(['offers']);
  const [updateStatus] = useUpdateOfferStatusMutation();
  const [updateFeedback] = useUpdateOfferFeedbackMutation();
  const [updateAvailability] = useUpdateOfferAvailabilityAndStatusMutation();
  const [updateMeetingTime] = useUpdateOfferMeetingTimeMutation();
  const [updateGeneratedPdf] = useUpdateOfferGeneratedPdfMutation();
  const [updateManualRequest] = useUpdateOfferSlotManualRequestMutation();
  const { add } = useNotifications();

  const updateOfferStatus = useCallback(
    (data: { status: OfferStatus; feedback_reject?: string; internal_notes_reject?: string }) => {
      if (!id) return;
      if (!opportunity) return;

      updateStatus({ id, opportunity, data })
        .unwrap()
        .then(() =>
          add({
            id: new Date().toString(),
            title: t('Offer updated'),
            description: t('An offer has been ' + data.status),
          })
        )
        .catch(() => {
          add({
            id: new Date().toString(),
            title: t('ERROR'),
            description: t('There was an error updating the offer. Please try again later.'),
          });
        })
        .finally(onClose);
    },
    [id, t, add, updateStatus, onClose, opportunity]
  );

  const updateOfferFeedback = useCallback(
    (data: Rating) => {
      if (!id) return;

      updateFeedback({ id, data })
        .unwrap()
        .then(() =>
          add({
            id: new Date().toString(),
            title: t('Feedback sent'),
            description: t('Your feedback has been sent'),
          })
        )
        .catch(() => {
          add({
            id: new Date().toString(),
            title: t('ERROR'),
            description: t('There was an error sending the feedback. Please try again later.'),
          });
        })
        .finally(onClose);
    },
    [id, t, add, updateFeedback, onClose]
  );

  const updateOfferAvailabilityAndStatus = useCallback(
    (data: { status: OfferStatus; availability: string[] }) => {
      if (!id) return;
      if (!opportunity) return;
      updateAvailability({ id, opportunity, data })
        .unwrap()
        .then(() =>
          add({
            id: new Date().toString(),
            title: t('Offer updated'),
            description: t('An offer has been ' + data.status),
          })
        )
        .catch(() => {
          add({
            id: new Date().toString(),
            title: t('ERROR'),
            description: t('There was an error updating the offer. Please try again later.'),
          });
        })
        .finally(onClose);
    },
    [id, t, add, updateAvailability, onClose, opportunity]
  );

  const updateOfferMeetingTime = useCallback(
    (data: { meeting_time: string }) => {
      if (!id) return;
      updateMeetingTime({ id, data })
        .unwrap()
        .then(() =>
          add({
            id: new Date().toString(),
            title: t('Offer updated'),
            description: t('A meeting time has been set for this offer.'),
          })
        )
        .catch(() => {
          add({
            id: new Date().toString(),
            title: t('ERROR'),
            description: t('There was an error updating the offer. Please try again later.'),
          });
        })
        .finally(onClose);
    },
    [id, t, add, updateMeetingTime, onClose]
  );

  const updateOfferSlotManualRequest = useCallback(
    (data: { request_manual_scheduling: boolean; manual_scheduling_availability: string | null }) => {
      if (!id) return;
      updateManualRequest({ id, data })
        .unwrap()
        .then(() => {
          add({
            id: new Date().toString(),
            title: t('Manual scheduling requested'),
            description: t('Our team will contact you to arrange a time that fits.'),
          });
        })
        .catch(() => {
          add({
            id: new Date().toString(),
            title: t('ERROR'),
            description: t('There was an error reqesting a manual slot. Please try again later.'),
          });
        })
        .finally(onClose);
    },
    [id, t, add, updateManualRequest, onClose]
  );

  const updateOfferGeneratedPdf = useCallback(
    (data: { id: string; submitted_eoi_file?: string; is_submitted_eoi_file_generated: boolean }) => {
      if (!id) return;
      const { id: offerId, ...fileData } = data;
      updateGeneratedPdf({ id: offerId, data: fileData })
        .unwrap()
        .catch(() => {
          add({
            id: new Date().toString(),
            title: t('ERROR'),
            description: t('There was an error updating the offer. Please try again later.'),
          });
        })
        .finally(onClose);
    },
    [id, t, add, updateGeneratedPdf, onClose]
  );

  return {
    updateOfferStatus,
    updateOfferFeedback,
    updateOfferAvailabilityAndStatus,
    updateOfferMeetingTime,
    updateOfferGeneratedPdf,
    updateOfferSlotManualRequest,
  };
}
